import { usePermissionStore } from '@/stores/permissions'
import { usePermissionCache } from '@/composables/usePermissions'
import { useAuth } from '@/composables/useAuth'
import type { PermissionValue } from '~/types/Permissions'

export default defineNuxtRouteMiddleware(async (to, from) => {
	const permissionStore = usePermissionStore()
	const { accountInfo } = useAuth()

	// Ignore if there's no account info
	if (!accountInfo.value?.id) {
		return
	}

	// Fetch permissions if not already loaded
	if (permissionStore.permissions.length === 0) {
		await permissionStore.fetchPermissions(accountInfo.value.id)
	}

	// Get required permissions from route meta
	const requiredPermission = to.meta.requiredPermission as PermissionValue | PermissionValue[] | undefined
	const requiredPermissionAll = to.meta.requiredPermissionAll as PermissionValue | PermissionValue[] | undefined
	const permissionDeniedMessage = to.meta.permissionDeniedMessage as string | undefined
	const permissionDeniedRedirect = to.meta.permissionDeniedRedirect as string | undefined

	// If no permissions required, allow access
	if (!requiredPermission && !requiredPermissionAll) {
		return
	}

	const { canAny, canAll } = usePermissionCache()

	let hasAccess = false

	// Check OR logic (at least one permission)
	if (requiredPermission) {
		const permissions = Array.isArray(requiredPermission) ? requiredPermission : [requiredPermission]
		hasAccess = canAny(permissions)
	}

	// Check AND logic (all permissions required)
	if (requiredPermissionAll && !hasAccess) {
		const permissions = Array.isArray(requiredPermissionAll) ? requiredPermissionAll : [requiredPermissionAll]
		hasAccess = canAll(permissions)
	}

	if (!hasAccess) {
		if (permissionDeniedRedirect) {
			return navigateTo(permissionDeniedRedirect)
		} else if (permissionDeniedMessage) {
			useNotification().alert({
				status: 300,
				message: permissionDeniedMessage
			})
		}

		return navigateTo('/')
	}
})
